import { useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  IconButton, Paper, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import api from '../services/api';
import { headCellSx, pairCellSx, pairRowSx, tableContainerSx } from '../quotas/QuotaTable';
import { wranglerError } from './errors';
import LogicSummary from './LogicSummary';
import RuleEditor, { NEW_RULE, scopeOptions } from './RuleEditor';
import { useWranglerQuestions, useWranglerQuotas, useWranglerRules } from './useWranglerData';

const plural = (n, word) => `${n} ${word}${n !== 1 ? 's' : ''}`;

export const scopeText = (config = {}) => {
  if (config.scope !== 'quotas') return 'All quotas';
  const quotas = config.quota_ids?.length || 0;
  const sets = config.set_names?.length || 0;
  return [quotas && plural(quotas, 'quota'), sets && plural(sets, 'set')].filter(Boolean).join(' and ') || 'No quotas';
};

/** The names a chosen scope covers, for the list's tooltip. */
const scopeNames = (config, quotas) => {
  if (config?.scope !== 'quotas') return null;
  const chosen = new Set([...(config.set_names || []).map((n) => `set:${n}`), ...(config.quota_ids || []).map((id) => `quota:${id}`)]);
  return scopeOptions(quotas, config).filter((o) => chosen.has(o.key))
    .map((o) => (o.type === 'set' ? `Set: ${o.name} (${plural(o.count, 'quota')} now)` : o.name)).join('\n');
};

/** The Rules tab (design §7): exemption rules, with the same slide-out editor as quotas. */
const RulesTab = ({ survey }) => {
  const surveyId = survey.id;
  const { rules, loading, error, reload, setData } = useWranglerRules(surveyId);
  const { quotas } = useWranglerQuotas(surveyId);
  const { questions } = useWranglerQuestions(surveyId);

  // {mode: 'edit', id} for a saved rule, {mode: 'create', key, draft?} for a new one or a copy.
  const [editor, setEditor] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [pending, setPending] = useState({});
  const [actionError, setActionError] = useState(null);

  const replaceRule = (updated) => setData((list) => list.map((r) => (r.id === updated.id ? updated : r)));
  const saved = (rule, { created } = {}) => (created ? setData((list) => [...list, rule]) : replaceRule(rule));
  const removed = (id) => setData((list) => list.filter((r) => r.id !== id));

  const run = async (rule, request, after) => {
    setPending((p) => ({ ...p, [rule.id]: true }));
    setActionError(null);
    try {
      const { data } = await request();
      after(data);
    } catch (err) {
      setActionError(`${rule.name}: ${wranglerError(err)}`);
    } finally {
      setPending((p) => ({ ...p, [rule.id]: false }));
    }
  };

  const setOn = (rule, isOn) => run(rule, () => api.patch(`/surveys/${surveyId}/wrangler/rules/${rule.id}/`, { is_on: isOn }), replaceRule);
  const confirmDelete = () => {
    const rule = deleting;
    setDeleting(null);
    run(rule, () => api.delete(`/surveys/${surveyId}/wrangler/rules/${rule.id}/`), () => removed(rule.id));
  };

  const open = editor?.mode === 'edit' ? rules.find((r) => r.id === editor.id) : null;
  // A copy opens OFF and is created only on Create, like a duplicated quota.
  const duplicate = (rule) => setEditor({
    mode: 'create', key: Date.now(),
    draft: { name: `Copy of ${rule.name}`, is_on: false, condition: rule.condition, config: rule.config },
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setEditor({ mode: 'create', key: Date.now() })}
          sx={{ textTransform: 'none', borderRadius: 2 }}>
          New rule
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 240 }}>
          Exemption rules let the people they describe past full quotas. They still count, even above 100%.
          Rules don&apos;t affect Alchemer&apos;s quotas.
        </Typography>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={reload}>Try again</Button>}>{error}</Alert>}
      {actionError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>{actionError}</Alert>}

      {loading && !rules.length ? <CircularProgress size={24} /> : rules.length === 0 && !error ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography color="text.secondary">This survey has no exemption rules.</Typography>
        </Paper>
      ) : rules.length > 0 && (
        <TableContainer component={Paper} elevation={0} sx={tableContainerSx}>
          <Table>
            <TableHead>
              <TableRow>
                {[{ id: 'name', label: 'Name' }, { id: 'on', label: 'ON/OFF' }, { id: 'scope', label: 'Lets them past' },
                  { id: 'who', label: 'Who' }, { id: 'actions', label: '', width: 48 }].map((col, i, cols) => (
                  <TableCell key={col.id} sx={{ ...headCellSx(col, i, cols.length), position: 'static' }}>{col.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map((rule, i) => {
                const cell = pairCellSx(true);
                const names = scopeNames(rule.config, quotas);
                return (
                  <TableRow key={rule.id} onClick={() => setEditor({ mode: 'edit', id: rule.id })}
                    sx={{ ...pairRowSx(i, i === rules.length - 1), cursor: 'pointer' }}>
                    <TableCell sx={cell}><Typography variant="body2" fontWeight={500}>{rule.name}</Typography></TableCell>
                    <TableCell sx={cell} onClick={(e) => e.stopPropagation()}>
                      <Tooltip title={rule.is_on ? 'Switch OFF' : 'Switch ON'}>
                        <span>
                          <Switch size="small" checked={rule.is_on} disabled={Boolean(pending[rule.id])}
                            onChange={(e) => setOn(rule, e.target.checked)} inputProps={{ 'aria-label': `${rule.name} ON` }} />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ ...cell, whiteSpace: 'nowrap' }}>
                      <Tooltip title={names ? <Box sx={{ whiteSpace: 'pre-line' }}>{names}</Box> : ''}>
                        <span>{scopeText(rule.config)}</span>
                      </Tooltip>
                      {rule.config?.include_overall && (
                        <Typography variant="caption" color="text.secondary" display="block">and the overall quota</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={cell}>
                      <LogicSummary logic={rule.condition} questions={questions} subject="Applies to people who" />
                    </TableCell>
                    <TableCell sx={cell} onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Delete">
                        <span>
                          <IconButton size="small" aria-label={`Delete ${rule.name}`} disabled={Boolean(pending[rule.id])}
                            onClick={() => setDeleting(rule)}>
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {open && (
        <RuleEditor key={`edit-${open.id}`} surveyId={surveyId} mode="edit" rule={open} quotas={quotas} questions={questions}
          onSaved={saved} onDeleted={removed} onClose={() => setEditor(null)} onDuplicate={duplicate} />
      )}
      {editor?.mode === 'create' && (
        <RuleEditor key={`create-${editor.key}`} surveyId={surveyId} mode="create" rule={editor.draft || NEW_RULE} quotas={quotas}
          questions={questions} onSaved={saved} onDeleted={removed} onClose={() => setEditor(null)} />
      )}

      <Dialog open={Boolean(deleting)} onClose={() => setDeleting(null)}>
        <DialogTitle>Delete {deleting?.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The people it describes are stopped by full quotas again. This can&apos;t be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete} sx={{ textTransform: 'none' }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RulesTab;
